import { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine,
} from 'recharts';
import {
  Activity, AlertCircle, AlertTriangle, Bell, CheckCircle2,
  Clock, Droplets, Filter as FilterIcon, Gauge, Info,
  Power, TrendingUp, Wrench, ChevronRight,
} from 'lucide-react';

/* ─── colour palette ─────────────────────────────────────────────── */
const C = {
  bg:     '#0a1628',
  card:   '#0d1f3c',
  card2:  '#0f2448',
  border: '#1e3a5f',
  blue:   '#3b82f6',
  cyan:   '#06b6d4',
  green:  '#22c55e',
  red:    '#ef4444',
  orange: '#f97316',
  yellow: '#eab308',
  gray:   '#6b7280',
  text:   '#f1f5f9',
  muted:  '#94a3b8',
  dim:    '#475569',
};

/* ─── tiny helpers ───────────────────────────────────────────────── */
const rand = (lo: number, hi: number, dp = 1) =>
  parseFloat((Math.random() * (hi - lo) + lo).toFixed(dp));
const fluct = (base: number, pct = 0.015) =>
  parseFloat((base + (Math.random() - 0.5) * base * pct * 2).toFixed(1));

type Tab = 'overview' | 'processFlow' | 'production' | 'equipment' | 'alarms' | 'maintenance';
type AlarmPriority = 'Critical' | 'Warning' | 'Attention' | 'Info' | 'Cleared';
type AlarmStatus  = 'Unacknowledged' | 'Acknowledged' | 'Cleared';

/* ─── static reference data ──────────────────────────────────────── */
const TANKS_BASE = [
  { name: 'Raw Water Tank',    level: 71.8, volume: 17950, capacity: 25000 },
  { name: 'Clear Water Tank',  level: 57.9, volume: 8684,  capacity: 15000 },
  { name: 'Treated Water Tank',level: 83.9, volume: 4195,  capacity: 5000  },
];

const PROCESS_STAGES = [
  { name: 'Raw Water\nIntake',   flow: 459,   unit: 'm³/hr', press: 1.2 },
  { name: 'Intake\nPump',        flow: 459,   unit: 'm³/hr', press: 2.4 },
  { name: 'Coagulation',         flow: 459,   unit: 'm³/hr', press: 2.1 },
  { name: 'Sedimentation',       flow: 459,   unit: 'm³/hr', press: 1.8 },
  { name: 'Sand\nFiltration',    flow: 449.8, unit: 'm³/hr', press: 1.4 },
  { name: 'Disinfection',        flow: 42.1,  unit: 'kg/d Cl',press: 1.4 },
  { name: 'Clear\nWater',        flow: 459,   unit: 'm³/hr', press: 1.2 },
  { name: 'Distribution',        flow: 436.0, unit: 'm³/hr', press: 4.8 },
];

const PUMPS = [
  { name:'Intake Pump 1', status:'RUNNING', runtime:3892, speed:82,  current:54.5, power:42.8, efficiency:89, health:88, lastMaint:'2023-10-15' },
  { name:'Intake Pump 2', status:'RUNNING', runtime:1240, speed:78,  current:79.8, power:37.5, efficiency:84, health:72, lastMaint:'2024-01-22' },
  { name:'Dist Pump 1',   status:'RUNNING', runtime:2450, speed:85,  current:33.5, power:20.6, efficiency:91, health:94, lastMaint:'2023-11-05' },
  { name:'Dist Pump 2',   status:'STOPPED', runtime:2100, speed:0,   current:0.0,  power:0.0,  efficiency:0,  health:91, lastMaint:'2023-12-10' },
];

const FILTERS_BASE = [
  { name:'Filter 1', status:'RUNNING',  pressureDiff:1.20, runtime:42, lifeLeft:320, mediaHealth:95 },
  { name:'Filter 2', status:'RUNNING',  pressureDiff:1.56, runtime:58, lifeLeft:290, mediaHealth:88 },
  { name:'Filter 3', status:'RUNNING',  pressureDiff:2.18, runtime:72, lifeLeft:150, mediaHealth:76 },
  { name:'Filter 4', status:'BACKWASH', pressureDiff:2.40, runtime:85, lifeLeft:180, mediaHealth:82 },
  { name:'Filter 5', status:'STANDBY',  pressureDiff:0.80, runtime:0,  lifeLeft:410, mediaHealth:98 },
  { name:'Filter 6', status:'RUNNING',  pressureDiff:1.10, runtime:24, lifeLeft:380, mediaHealth:94 },
];

const ALARMS_INIT = [
  { id:1, time:'2:47:26 PM', priority:'Critical'  as AlarmPriority, equipment:'Filter 3',       description:'High pressure differential (2.8 bar)', status:'Unacknowledged' as AlarmStatus },
  { id:2, time:'2:20:26 PM', priority:'Warning'   as AlarmPriority, equipment:'Intake Pump 2',  description:'High current draw (72A)',               status:'Unacknowledged' as AlarmStatus },
  { id:3, time:'1:02:26 PM', priority:'Attention' as AlarmPriority, equipment:'Clear Water Tank',description:'Level below 55%',                      status:'Acknowledged'  as AlarmStatus },
  { id:4, time:'11:02:26 AM',priority:'Critical'  as AlarmPriority, equipment:'Chlorine System', description:'Low chlorine level (0.4 mg/L)',         status:'Unacknowledged' as AlarmStatus },
  { id:5, time:'9:02:26 AM', priority:'Info'      as AlarmPriority, equipment:'Dist Pump 1',    description:'Scheduled maintenance in 48 hrs',        status:'Acknowledged'  as AlarmStatus },
  { id:6, time:'6:42:26 AM', priority:'Cleared'   as AlarmPriority, equipment:'Filter 1',       description:'Backwash completed',                     status:'Cleared'       as AlarmStatus },
];

const MAINT = [
  { id:1, equipment:'Intake Pump 1',   task:'Bearing inspection & lubrication',  due:'2024-03-15', priority:'Medium', status:'Scheduled', assignee:'Team A' },
  { id:2, equipment:'Filter 3',        task:'Media replacement',                  due:'2024-02-28', priority:'High',   status:'Overdue',   assignee:'Team B' },
  { id:3, equipment:'Chlorine System', task:'Dosing pump calibration',            due:'2024-03-01', priority:'High',   status:'Scheduled', assignee:'Team A' },
  { id:4, equipment:'Dist Pump 2',     task:'Motor inspection',                   due:'2024-03-20', priority:'Low',    status:'Planned',   assignee:'Team C' },
  { id:5, equipment:'All Filters',     task:'Quarterly backwash cycle',           due:'2024-03-31', priority:'Medium', status:'Planned',   assignee:'Team B' },
  { id:6, equipment:'Intake Pump 2',   task:'Impeller clearance check',           due:'2024-04-10', priority:'Low',    status:'Planned',   assignee:'Team A' },
];

/* ─── sub-components ─────────────────────────────────────────────── */

function TankVisual({ tank }: { tank: typeof TANKS_BASE[0] }) {
  const fillColor = tank.level > 70 ? C.blue : tank.level > 40 ? C.cyan : C.red;
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:8 }}>
      <div style={{ fontSize:11, color:C.muted, fontWeight:500, textAlign:'center' }}>{tank.name}</div>
      <div style={{ position:'relative', width:80, height:100, border:`2px solid ${C.border}`, borderRadius:4, overflow:'hidden', background:'#060f1e' }}>
        <div style={{ position:'absolute', left:0, right:0, top:'2%', borderTop:`1px dashed ${C.red}`, zIndex:2 }} />
        <span style={{ position:'absolute', top:'3%', right:2, fontSize:8, color:C.red, zIndex:3 }}>98%</span>
        <div style={{ position:'absolute', bottom:0, left:0, right:0, height:`${tank.level}%`, background:`linear-gradient(180deg,${fillColor}90 0%,${fillColor}cc 100%)`, transition:'height 1.5s ease' }} />
        <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:700, fontSize:15, zIndex:4 }}>
          {tank.level.toFixed(1)}%
        </div>
      </div>
      <div style={{ textAlign:'center' }}>
        <div style={{ fontSize:12, color:C.text, fontWeight:600 }}>{tank.volume.toLocaleString()} <span style={{ fontSize:10, color:C.muted }}>m³</span></div>
        <div style={{ fontSize:10, color:C.dim }}>Capacity: {tank.capacity.toLocaleString()} m³</div>
      </div>
    </div>
  );
}

function CircleGauge({ value, size = 54 }: { value: number; size?: number }) {
  const color = value >= 85 ? C.green : value >= 65 ? C.orange : C.red;
  const r = (size - 8) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <svg width={size} height={size}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={C.border} strokeWidth={6} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={6}
        strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`} />
      <text x={size/2} y={size/2+5} textAnchor="middle" fill="white" fontSize={11} fontWeight="bold">{value}%</text>
    </svg>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string,{bg:string;color:string}> = {
    RUNNING:  { bg:'#14532d', color:C.green  },
    STOPPED:  { bg:'#27272a', color:C.gray   },
    BACKWASH: { bg:'#1e3a5f', color:C.blue   },
    STANDBY:  { bg:'#1c1917', color:'#a8a29e'},
  };
  const s = cfg[status] ?? cfg.STOPPED;
  return (
    <span style={{ background:s.bg, color:s.color, fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:12, border:`1px solid ${s.color}40` }}>
      • {status}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: AlarmPriority }) {
  const cfg: Record<AlarmPriority,{bg:string;color:string;icon:string}> = {
    Critical:  { bg:'#450a0a', color:C.red,    icon:'⊘' },
    Warning:   { bg:'#431407', color:C.orange,  icon:'△' },
    Attention: { bg:'#1e3a5f', color:C.blue,    icon:'ⓘ' },
    Info:      { bg:'#1e3a5f', color:'#818cf8', icon:'ⓘ' },
    Cleared:   { bg:'#14532d', color:C.green,   icon:'✓' },
  };
  const s = cfg[priority];
  return (
    <span style={{ background:s.bg, color:s.color, fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:4, border:`1px solid ${s.color}50` }}>
      {s.icon} {priority}
    </span>
  );
}

/* ─── main component ─────────────────────────────────────────────── */
export function WaterPlantMonitor() {
  const [tab, setTab] = useState<Tab>('overview');
  const [tanks, setTanks] = useState(TANKS_BASE.map(t => ({ ...t })));
  const [flowRate, setFlowRate]   = useState(441);
  const [prodToday, setProdToday] = useState(126.1);
  const [totalFilt, setTotalFilt] = useState(2878);
  const [avgFlow, setAvgFlow]     = useState(459);
  const [chlorine, setChlorine]   = useState(42.1);
  const [alarms, setAlarms]       = useState(ALARMS_INIT);
  const [pFilter, setPFilter]     = useState('All Priorities');
  const [sFilter, setSFilter]     = useState('All Statuses');

  const [flowTrend, setFlowTrend] = useState<{t:string;v:number}[]>(() =>
    Array.from({ length:20 }, (_,i) => ({ t:`-${(20-i)*3}s`, v: rand(440, 464) }))
  );

  const [hourly] = useState(() =>
    Array.from({ length:23 }, (_,i) => ({ h:`${String(i+1).padStart(2,'0')}:00`, v: rand(310,490,0) }))
  );
  const [daily] = useState(() =>
    ['Wed','Thu','Fri','Sat','Sun','Mon','Tue'].map(d => ({ d, actual: rand(110,128,1) }))
  );

  useEffect(() => {
    const iv = setInterval(() => {
      setFlowRate(fluct(441, 0.02));
      setAvgFlow(fluct(459, 0.01));
      setProdToday(f => parseFloat((f + rand(0, 0.05, 2)).toFixed(1)));
      setTotalFilt(f => f + rand(0, 2, 0));
      setChlorine(fluct(42.1, 0.02));
      setTanks(prev => prev.map(t => ({ ...t, level: parseFloat(fluct(t.level, 0.004).toFixed(1)) })));
      setFlowTrend(prev => {
        const next = [...prev.slice(1), { t:'0s', v: fluct(450, 0.03) }];
        return next.map((p,i) => ({ ...p, t:`-${(next.length-1-i)*3}s` }));
      });
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  const critCount   = alarms.filter(a => a.priority==='Critical'  && a.status!=='Cleared').length;
  const warnCount   = alarms.filter(a => a.priority==='Warning'   && a.status!=='Cleared').length;
  const attnCount   = alarms.filter(a => a.priority==='Attention' && a.status!=='Cleared').length;
  const prodPct     = ((prodToday / 130) * 100).toFixed(1);
  const prodColor   = parseFloat(prodPct) >= 95 ? C.green : C.orange;

  const filteredAlarms = alarms.filter(a =>
    (pFilter === 'All Priorities' || a.priority === pFilter) &&
    (sFilter === 'All Statuses'  || a.status  === sFilter)
  );

  const ack = (id: number) =>
    setAlarms(prev => prev.map(a => a.id===id ? { ...a, status:'Acknowledged' as AlarmStatus } : a));

  /* shared card style */
  const card = { background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:14 } as const;

  const TABS: { id:Tab; label:string }[] = [
    { id:'overview',     label:'Overview'     },
    { id:'processFlow',  label:'Process Flow' },
    { id:'production',   label:'Production'   },
    { id:'equipment',    label:'Equipment'    },
    { id:'alarms',       label:'Alarms'       },
    { id:'maintenance',  label:'Maintenance'  },
  ];

  return (
    <div style={{ background:C.bg, minHeight:'100%', color:C.text, fontFamily:'inherit', borderRadius:12, overflow:'hidden' }}>

      {/* ── Tab bar ──────────────────────────────────────────────── */}
      <div style={{ display:'flex', justifyContent:'center', padding:'10px 0', borderBottom:`1px solid ${C.border}`, background:'#060f1e' }}>
        <div style={{ display:'flex', gap:2 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding:'7px 16px', borderRadius:6, border:'none', cursor:'pointer',
              fontSize:13, fontWeight:500, transition:'all .15s',
              background: tab===t.id ? C.blue : 'transparent',
              color: tab===t.id ? 'white' : C.muted,
              position:'relative',
            }}>
              {t.label}
              {t.id==='alarms' && (critCount+warnCount)>0 && (
                <span style={{ position:'absolute', top:5, right:5, width:6, height:6, borderRadius:'50%', background:C.red }} />
              )}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding:16, display:'flex', flexDirection:'column', gap:14 }}>

        {/* ══════════════════ OVERVIEW ══════════════════ */}
        {tab==='overview' && (<>

          {/* KPI row 1 */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            <div style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5 }}><Activity size={12} color={C.green}/>Plant Status</span>
                <span style={{ background:'#14532d', color:C.green, fontSize:10, padding:'1px 7px', borderRadius:10, fontWeight:600 }}>ONLINE</span>
              </div>
              <div style={{ fontSize:22, fontWeight:700, marginTop:8, color:C.green }}>RUNNING</div>
            </div>
            <div style={card}>
              <div style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5 }}><Droplets size={12} color={C.blue}/>Production Today</div>
              <div style={{ marginTop:8 }}><span style={{ fontSize:24, fontWeight:700 }}>{prodToday.toFixed(1)}</span><span style={{ fontSize:11, color:C.muted, marginLeft:4 }}>MLD</span></div>
              <div style={{ fontSize:11, color:C.green, marginTop:2 }}>+2.4% vs yday</div>
            </div>
            <div style={card}>
              <div style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5 }}><Gauge size={12} color={C.blue}/>Production Target</div>
              <div style={{ marginTop:8 }}><span style={{ fontSize:24, fontWeight:700 }}>130.0</span><span style={{ fontSize:11, color:C.muted, marginLeft:4 }}>MLD</span></div>
            </div>
            <div style={card}>
              <div style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5 }}><TrendingUp size={12} color={C.green}/>Prod vs Target</div>
              <div style={{ fontSize:24, fontWeight:700, marginTop:8, color:prodColor }}>{prodPct}%</div>
            </div>
          </div>

          {/* KPI row 2 */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            <div style={card}>
              <div style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5 }}><FilterIcon size={12} color={C.blue}/>Total Filtered</div>
              <div style={{ marginTop:8 }}><span style={{ fontSize:22, fontWeight:700 }}>{Math.round(totalFilt).toLocaleString()}</span><span style={{ fontSize:11, color:C.muted, marginLeft:4 }}>ML</span></div>
            </div>
            <div style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5 }}><Activity size={12} color={C.blue}/>Flow Rate</span>
                <div style={{ width:80, height:28 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={flowTrend.slice(-10)}>
                      <Line type="monotone" dataKey="v" stroke={C.blue} strokeWidth={1.5} dot={false} isAnimationActive={false}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div style={{ marginTop:4 }}><span style={{ fontSize:22, fontWeight:700 }}>{Math.round(flowRate)}</span><span style={{ fontSize:11, color:C.muted, marginLeft:4 }}>m³/hr</span></div>
            </div>
            <div style={{ ...card, borderColor: critCount>0 ? `${C.red}50` : C.border }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5 }}><Bell size={12} color={C.red}/>Active Alarms</span>
                {critCount>0 && <span style={{ background:'#450a0a', color:C.red, fontSize:10, padding:'1px 7px', borderRadius:10, fontWeight:600, border:`1px solid ${C.red}40` }}>CRITICAL</span>}
              </div>
              <div style={{ fontSize:30, fontWeight:700, marginTop:6, color:critCount>0?C.red:C.text }}>{critCount+warnCount+attnCount}</div>
            </div>
          </div>

          {/* Tanks + OEE */}
          <div style={{ display:'grid', gridTemplateColumns:'3fr 2fr', gap:12 }}>
            <div style={card}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:3, height:14, background:C.blue, borderRadius:2 }}/>Tank Monitoring
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
                {tanks.map(t => <TankVisual key={t.name} tank={t}/>)}
              </div>
            </div>
            <div style={card}>
              <div style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Overall Equipment Effectiveness</div>
              <div style={{ fontSize:10, color:C.dim, marginBottom:10 }}>OEE = Availability × Performance × Quality</div>
              <div style={{ display:'flex', gap:14, alignItems:'center' }}>
                <div style={{ position:'relative', width:100, height:100, flexShrink:0 }}>
                  <PieChart width={100} height={100}>
                    <Pie data={[{v:94.2},{v:5.8}]} cx={45} cy={45} innerRadius={30} outerRadius={44} startAngle={90} endAngle={-270} dataKey="v" strokeWidth={0}>
                      <Cell fill={C.blue}/><Cell fill={C.border}/>
                    </Pie>
                  </PieChart>
                  <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ fontSize:17, fontWeight:700 }}>94.2%</span>
                    <span style={{ fontSize:9, color:C.muted }}>OEE</span>
                  </div>
                </div>
                <div style={{ flex:1, display:'flex', flexDirection:'column', gap:7 }}>
                  {[['Availability',96.4],['Performance',81.8],['Quality',98.1],['Equip Avail.',96.4]].map(([l,v]) => (
                    <div key={String(l)}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, marginBottom:2 }}>
                        <span style={{ color:C.muted }}>{l}</span><span style={{ fontWeight:600 }}>{v}%</span>
                      </div>
                      <div style={{ height:3, background:C.border, borderRadius:2 }}>
                        <div style={{ height:'100%', width:`${v}%`, background:C.blue, borderRadius:2 }}/>
                      </div>
                    </div>
                  ))}
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:10 }}>
                    <span style={{ color:C.muted }}>Downtime (today)</span>
                    <span style={{ color:C.red, fontWeight:600 }}>18 min</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pumps + Filtration + Recent Alarms */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                <span style={{ fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}><Power size={13} color={C.blue}/>Pump Stations</span>
                <span style={{ fontSize:11, color:C.muted }}>{PUMPS.filter(p=>p.status==='RUNNING').length}/{PUMPS.length} Running</span>
              </div>
              {PUMPS.map(p => (
                <div key={p.name} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 0', borderBottom:`1px solid ${C.border}20` }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <span style={{ width:7, height:7, borderRadius:'50%', background:p.status==='RUNNING'?C.green:C.gray, display:'inline-block', flexShrink:0 }}/>
                    <span style={{ fontSize:12 }}>{p.name}</span>
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <span style={{ fontSize:11, color:C.muted }}>{p.speed}% · {p.current}A</span>
                    <StatusBadge status={p.status}/>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <div style={card}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                  <span style={{ fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}><FilterIcon size={13} color={C.blue}/>Filtration System</span>
                  <span style={{ fontSize:11, color:C.muted }}>{FILTERS_BASE.filter(f=>f.status==='RUNNING').length}/{FILTERS_BASE.length} Active</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
                  {FILTERS_BASE.map(f => (
                    <div key={f.name} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:11, display:'flex', alignItems:'center', gap:5 }}>
                        <span style={{ width:6, height:6, borderRadius:'50%', background:f.status==='RUNNING'?C.green:f.status==='BACKWASH'?C.blue:C.gray, display:'inline-block' }}/>
                        {f.name}
                      </span>
                      <StatusBadge status={f.status}/>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ ...card, flex:1 }}>
                <div style={{ fontSize:13, fontWeight:600, marginBottom:10, display:'flex', alignItems:'center', gap:6 }}><AlertTriangle size={13} color={C.orange}/>Recent Alarms</div>
                {ALARMS_INIT.slice(0,3).map(a => (
                  <div key={a.id} style={{ display:'flex', alignItems:'flex-start', gap:6, fontSize:11, marginBottom:8 }}>
                    <span style={{ color:a.priority==='Critical'?C.red:a.priority==='Warning'?C.orange:C.blue, flexShrink:0, marginTop:1 }}>•</span>
                    <span style={{ flex:1, color:C.muted }}>{a.description}</span>
                    <button onClick={() => ack(a.id)} style={{ fontSize:10, padding:'2px 7px', borderRadius:4, border:`1px solid ${C.border}`, background:'transparent', color:a.status==='Unacknowledged'?C.blue:C.dim, cursor:'pointer', flexShrink:0 }}>
                      {a.status==='Unacknowledged'?'UNACK':'ACK'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>)}

        {/* ══════════════════ PROCESS FLOW ══════════════════ */}
        {tab==='processFlow' && (<>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
            <div>
              <h2 style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>Live Process Flow</h2>
              <p style={{ fontSize:12, color:C.muted }}>Real-time water treatment process — scroll to view all stages</p>
            </div>
            <div style={{ display:'flex', gap:14, fontSize:11 }}>
              <span><span style={{ color:C.green }}>●</span> Running</span>
              <span><span style={{ color:C.orange }}>●</span> Warning</span>
              <span><span style={{ color:C.gray }}>●</span> Stopped</span>
            </div>
          </div>

          <div style={{ ...card, overflowX:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', minWidth:'max-content', padding:'6px 0', gap:0 }}>
              {PROCESS_STAGES.map((s, i) => (
                <div key={s.name} style={{ display:'flex', alignItems:'center' }}>
                  <div style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:8, padding:'12px 14px', minWidth:138 }}>
                    <div style={{ fontSize:10, color:C.green, display:'flex', alignItems:'center', gap:4, marginBottom:7 }}>
                      <span style={{ width:5, height:5, borderRadius:'50%', background:C.green, display:'inline-block' }}/>RUNNING
                    </div>
                    <div style={{ fontSize:12, fontWeight:600, whiteSpace:'pre-line', lineHeight:1.3, marginBottom:8 }}>{s.name}</div>
                    <div style={{ fontSize:11, color:C.muted, display:'flex', flexDirection:'column', gap:3 }}>
                      <div><span style={{ color:C.dim }}>Flow:&nbsp;</span>{s.flow} <span style={{ color:C.dim }}>{s.unit}</span></div>
                      <div><span style={{ color:C.dim }}>Press:&nbsp;</span>{s.press} <span style={{ color:C.dim }}>bar</span></div>
                    </div>
                  </div>
                  {i < PROCESS_STAGES.length-1 && (
                    <div style={{ display:'flex', alignItems:'center', padding:'0 2px', color:C.blue, fontSize:11, gap:1 }}>
                      <span style={{ letterSpacing:-1 }}>──</span><ChevronRight size={14}/>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              { icon:Clock,       label:'Uptime Today',  value:'18 hrs',               sub:'100% availability'  },
              { icon:Activity,    label:'Avg Flow Rate', value:`${avgFlow} m³/hr`,      sub:'Within normal range'},
              { icon:Droplets,    label:'Chlorine Dose', value:`${chlorine.toFixed(1)} kg/d`, sub:'Residual 0.5 mg/L' },
              { icon:CheckCircle2,label:'Stages Active', value:`${PROCESS_STAGES.length} / ${PROCESS_STAGES.length}`, sub:'All stages running'},
            ].map(item => (
              <div key={item.label} style={card}>
                <div style={{ fontSize:11, color:C.muted, display:'flex', alignItems:'center', gap:5, marginBottom:8 }}>
                  <item.icon size={12} color={C.blue}/>{item.label}
                </div>
                <div style={{ fontSize:22, fontWeight:700 }}>{item.value}</div>
                <div style={{ fontSize:11, color:C.green, marginTop:4 }}>{item.sub}</div>
              </div>
            ))}
          </div>
        </>)}

        {/* ══════════════════ PRODUCTION ══════════════════ */}
        {tab==='production' && (<>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
                <span style={{ fontSize:13, fontWeight:600 }}>Hourly Production (m³/hr)</span>
                <span style={{ fontSize:11, color:C.muted }}>Last 24 Hours</span>
              </div>
              <div style={{ height:210 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hourly} margin={{ top:0, right:0, bottom:0, left:-10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="h" tick={{ fill:C.muted, fontSize:9 }} tickLine={false} axisLine={false} interval={3}/>
                    <YAxis tick={{ fill:C.muted, fontSize:9 }} tickLine={false} axisLine={false}/>
                    <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6 }} labelStyle={{ color:C.muted }} itemStyle={{ color:C.blue }}/>
                    <Bar dataKey="v" name="m³/hr" fill={C.blue} radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={card}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
                <span style={{ fontSize:13, fontWeight:600 }}>Live Flow Rate Trend (m³/hr)</span>
                <span style={{ fontSize:11, color:C.green, display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:C.green, display:'inline-block' }}/>Live
                </span>
              </div>
              <div style={{ height:210 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={flowTrend} margin={{ top:0, right:0, bottom:0, left:-10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                    <XAxis dataKey="t" tick={{ fill:C.muted, fontSize:9 }} tickLine={false} axisLine={false} interval={3}/>
                    <YAxis tick={{ fill:C.muted, fontSize:9 }} tickLine={false} axisLine={false} domain={['auto','auto']}/>
                    <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6 }} labelStyle={{ color:C.muted }} itemStyle={{ color:C.cyan }}/>
                    <Line type="monotone" dataKey="v" name="m³/hr" stroke={C.cyan} strokeWidth={2} dot={false} isAnimationActive={false}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
          <div style={card}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:14 }}>
              <span style={{ fontSize:13, fontWeight:600 }}>Daily Production vs Target (MLD)</span>
              <span style={{ fontSize:11, color:C.muted }}>Last 7 Days</span>
            </div>
            <div style={{ height:210 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} margin={{ top:0, right:10, bottom:0, left:-10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false}/>
                  <XAxis dataKey="d" tick={{ fill:C.muted, fontSize:11 }} tickLine={false} axisLine={false}/>
                  <YAxis tick={{ fill:C.muted, fontSize:10 }} tickLine={false} axisLine={false}/>
                  <Tooltip contentStyle={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:6 }} labelStyle={{ color:C.muted }}/>
                  <ReferenceLine y={130} stroke={C.orange} strokeDasharray="4 4" strokeWidth={1.5}/>
                  <Bar dataKey="actual" name="Actual Production" fill={C.blue} radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display:'flex', gap:16, marginTop:8, fontSize:11, justifyContent:'center' }}>
              <span style={{ display:'flex', alignItems:'center', gap:6 }}><span style={{ width:12, height:10, background:C.blue, borderRadius:2, display:'inline-block' }}/>Actual Production</span>
              <span style={{ display:'flex', alignItems:'center', gap:6 }}><span style={{ width:12, height:2, background:C.orange, display:'inline-block' }}/>Target (130 MLD)</span>
            </div>
          </div>
        </>)}

        {/* ══════════════════ EQUIPMENT ══════════════════ */}
        {tab==='equipment' && (<>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              { label:'Equip Availability', value:'96.4%', color:C.green  },
              { label:'OEE',                value:'94.2%', color:C.blue   },
              { label:'MTBF',               value:'847 hrs', color:C.text },
              { label:'MTTR',               value:'2.3 hrs', color:C.text },
            ].map(k => (
              <div key={k.label} style={card}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>{k.label}</div>
                <div style={{ fontSize:24, fontWeight:700, color:k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:3, height:14, background:C.blue, borderRadius:2 }}/>Pump Stations
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
              {PUMPS.map(p => (
                <div key={p.name} style={card}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:600 }}>{p.name}</div>
                      <div style={{ fontSize:10, color:C.muted, marginTop:2, display:'flex', alignItems:'center', gap:3 }}><Clock size={9}/>{p.runtime.toLocaleString()} hrs</div>
                    </div>
                    <StatusBadge status={p.status}/>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12 }}>
                    {[['Speed',`${p.speed}%`],['Current',`${p.current} A`],['Power',`${p.power} kW`],['Efficiency',`${p.efficiency}%`]].map(([l,v]) => (
                      <div key={String(l)}>
                        <div style={{ fontSize:10, color:C.dim }}>{l}</div>
                        <div style={{ fontSize:14, fontWeight:600, color:l==='Efficiency'&&p.efficiency>80?C.blue:C.text }}>{v}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <CircleGauge value={p.health} size={50}/>
                    <div>
                      <div style={{ fontSize:10, color:C.muted }}>Health Score</div>
                      <div style={{ fontSize:10, color:C.dim, marginTop:2 }}>Last Maint: {p.lastMaint}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:3, height:14, background:C.blue, borderRadius:2 }}/>Filtration System
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
              {FILTERS_BASE.map(f => (
                <div key={f.name} style={card}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                    <span style={{ fontSize:13, fontWeight:600, display:'flex', alignItems:'center', gap:6 }}><FilterIcon size={12} color={C.blue}/>{f.name}</span>
                    <StatusBadge status={f.status}/>
                  </div>
                  <div style={{ marginBottom:10 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:4 }}>
                      <span style={{ color:C.muted }}>Pressure Diff</span>
                      <span style={{ color:f.pressureDiff>2?C.red:C.blue, fontWeight:600 }}>{f.pressureDiff.toFixed(2)} bar</span>
                    </div>
                    <div style={{ height:4, background:C.border, borderRadius:2 }}>
                      <div style={{ height:'100%', width:`${(f.pressureDiff/3)*100}%`, background:f.pressureDiff>2?C.red:C.blue, borderRadius:2 }}/>
                    </div>
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                    <div>
                      <div style={{ fontSize:10, color:C.dim, display:'flex', alignItems:'center', gap:3 }}><Clock size={8}/>RUNTIME</div>
                      <div style={{ fontSize:14, fontWeight:600 }}>{f.runtime}h</div>
                    </div>
                    <div>
                      <div style={{ fontSize:10, color:C.dim }}>LIFE LEFT</div>
                      <div style={{ fontSize:14, fontWeight:600, color:f.lifeLeft<200?C.orange:C.text }}>{f.lifeLeft}d</div>
                    </div>
                  </div>
                  <div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:4 }}>
                      <span style={{ color:C.muted }}>Media Health</span><span style={{ fontWeight:600 }}>{f.mediaHealth}%</span>
                    </div>
                    <div style={{ height:4, background:C.border, borderRadius:2 }}>
                      <div style={{ height:'100%', width:`${f.mediaHealth}%`, background:C.blue, borderRadius:2 }}/>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>)}

        {/* ══════════════════ ALARMS ══════════════════ */}
        {tab==='alarms' && (<>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            <div style={{ ...card, border:`1px solid ${C.red}40`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}><AlertCircle size={20} color={C.red}/><span style={{ fontSize:14, fontWeight:500 }}>Critical Alarms</span></div>
              <span style={{ fontSize:28, fontWeight:700, color:C.red }}>{critCount}</span>
            </div>
            <div style={{ ...card, border:`1px solid ${C.orange}40`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}><AlertTriangle size={20} color={C.orange}/><span style={{ fontSize:14, fontWeight:500 }}>Warnings</span></div>
              <span style={{ fontSize:28, fontWeight:700, color:C.orange }}>{warnCount}</span>
            </div>
            <div style={{ ...card, border:`1px solid ${C.blue}40`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}><Info size={20} color={C.blue}/><span style={{ fontSize:14, fontWeight:500 }}>Attention Needed</span></div>
              <span style={{ fontSize:28, fontWeight:700, color:C.blue }}>{attnCount}</span>
            </div>
          </div>

          <div style={card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
              <span style={{ fontSize:14, fontWeight:600 }}>Active Alarms</span>
              <div style={{ display:'flex', gap:8 }}>
                {['All Priorities','Critical','Warning','Attention','Info','Cleared'].map(o => null).slice(0,0)}
                <select value={pFilter} onChange={e => setPFilter(e.target.value)} style={{ background:C.card2, color:C.text, border:`1px solid ${C.border}`, borderRadius:6, padding:'4px 8px', fontSize:12, cursor:'pointer' }}>
                  {['All Priorities','Critical','Warning','Attention','Info','Cleared'].map(o => <option key={o}>{o}</option>)}
                </select>
                <select value={sFilter} onChange={e => setSFilter(e.target.value)} style={{ background:C.card2, color:C.text, border:`1px solid ${C.border}`, borderRadius:6, padding:'4px 8px', fontSize:12, cursor:'pointer' }}>
                  {['All Statuses','Unacknowledged','Acknowledged','Cleared'].map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'110px 100px 150px 1fr 140px 110px', padding:'6px 8px', fontSize:10, color:C.dim, fontWeight:600, letterSpacing:'0.05em', borderBottom:`1px solid ${C.border}` }}>
              <span>TIME</span><span>PRIORITY</span><span>EQUIPMENT</span><span>DESCRIPTION</span><span>STATUS</span><span style={{ textAlign:'right' }}>ACTION</span>
            </div>
            {filteredAlarms.map(a => (
              <div key={a.id} style={{ display:'grid', gridTemplateColumns:'110px 100px 150px 1fr 140px 110px', padding:'10px 8px', fontSize:12, borderBottom:`1px solid ${C.border}20`, alignItems:'center' }}>
                <span style={{ color:C.muted }}>{a.time}</span>
                <span><PriorityBadge priority={a.priority}/></span>
                <span style={{ fontWeight:500 }}>{a.equipment}</span>
                <span style={{ color:C.muted }}>{a.description}</span>
                <span style={{ color:a.status==='Unacknowledged'?C.red:a.status==='Acknowledged'?C.blue:C.green, fontWeight:500 }}>
                  {a.status==='Unacknowledged' && <span style={{ marginRight:4 }}>●</span>}{a.status}
                </span>
                <span style={{ textAlign:'right' }}>
                  {a.status==='Unacknowledged' && (
                    <button onClick={() => ack(a.id)} style={{ background:C.border, color:C.blue, border:`1px solid ${C.blue}40`, borderRadius:4, padding:'3px 10px', fontSize:11, cursor:'pointer', fontWeight:500 }}>
                      Acknowledge
                    </button>
                  )}
                </span>
              </div>
            ))}
            {filteredAlarms.length===0 && (
              <div style={{ padding:32, textAlign:'center', color:C.muted }}>No alarms match the selected filters</div>
            )}
          </div>
        </>)}

        {/* ══════════════════ MAINTENANCE ══════════════════ */}
        {tab==='maintenance' && (<>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12 }}>
            {[
              { label:'Overdue',   count:MAINT.filter(m=>m.status==='Overdue').length,   color:C.red    },
              { label:'Scheduled', count:MAINT.filter(m=>m.status==='Scheduled').length, color:C.orange },
              { label:'Planned',   count:MAINT.filter(m=>m.status==='Planned').length,   color:C.blue   },
            ].map(s => (
              <div key={s.label} style={{ ...card, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}><Wrench size={18} color={s.color}/><span style={{ fontSize:13, fontWeight:500 }}>{s.label}</span></div>
                <span style={{ fontSize:28, fontWeight:700, color:s.color }}>{s.count}</span>
              </div>
            ))}
          </div>
          <div style={card}>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:14 }}>Maintenance Schedule</div>
            <div style={{ display:'grid', gridTemplateColumns:'170px 1fr 100px 80px 100px 90px', padding:'6px 8px', fontSize:10, color:C.dim, fontWeight:600, letterSpacing:'0.05em', borderBottom:`1px solid ${C.border}` }}>
              <span>EQUIPMENT</span><span>TASK</span><span>DUE DATE</span><span>PRIORITY</span><span>STATUS</span><span>ASSIGNEE</span>
            </div>
            {MAINT.map(m => {
              const sc = m.status==='Overdue'?C.red:m.status==='Scheduled'?C.orange:C.blue;
              const pc = m.priority==='High'?C.red:m.priority==='Medium'?C.orange:C.muted;
              return (
                <div key={m.id} style={{ display:'grid', gridTemplateColumns:'170px 1fr 100px 80px 100px 90px', padding:'10px 8px', fontSize:12, borderBottom:`1px solid ${C.border}20`, alignItems:'center' }}>
                  <span style={{ fontWeight:500 }}>{m.equipment}</span>
                  <span style={{ color:C.muted }}>{m.task}</span>
                  <span style={{ color:C.muted }}>{m.due}</span>
                  <span style={{ color:pc, fontWeight:500 }}>{m.priority}</span>
                  <span><span style={{ background:`${sc}20`, color:sc, fontSize:10, padding:'2px 8px', borderRadius:10, fontWeight:600 }}>{m.status}</span></span>
                  <span style={{ color:C.muted }}>{m.assignee}</span>
                </div>
              );
            })}
          </div>
        </>)}

      </div>
    </div>
  );
}
